import { useEffect, useState } from 'react';

import {
  CHAT_READ_EVENT,
  fetchUnreadChatCount,
} from 'mastodon/features/direct_chat/api';
import {
  selectAppBadgeNotificationCount,
  selectDirectMentionKey,
} from 'mastodon/selectors/notifications';
import { useAppSelector } from 'mastodon/store';
import { setAppBadgeCount } from 'mastodon/utils/app_badge';

// Notifications and read markers load one after the other at start: wait for
// the count to settle before showing it
const SETTLE_DELAY = 1500;

/**
 * Puts the number of unread notifications plus unread DM chats on the app
 * icon, where the device shows one. Renders nothing.
 */
export const AppBadge: React.FC = () => {
  const notificationCount = useAppSelector(selectAppBadgeNotificationCount);
  const directMentionKey = useAppSelector(selectDirectMentionKey);
  const [chatCount, setChatCount] = useState<number | null>(null);
  const [chatReloads, setChatReloads] = useState(0);

  // Reload the unread chats when a chat is read here, when the app comes
  // back on screen (it may have been read elsewhere) and when a DM arrives
  useEffect(() => {
    const reload = () => {
      if (document.visibilityState === 'visible') {
        setChatReloads((n) => n + 1);
      }
    };

    window.addEventListener(CHAT_READ_EVENT, reload);
    document.addEventListener('visibilitychange', reload);

    return () => {
      window.removeEventListener(CHAT_READ_EVENT, reload);
      document.removeEventListener('visibilitychange', reload);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    fetchUnreadChatCount()
      .then((count) => {
        if (!cancelled) setChatCount(count);
      })
      .catch(() => {
        // Keep the last count
      });

    return () => {
      cancelled = true;
    };
  }, [directMentionKey, chatReloads]);

  useEffect(() => {
    if (chatCount === null) return;

    const timer = setTimeout(() => {
      void setAppBadgeCount(notificationCount + chatCount);
    }, SETTLE_DELAY);

    return () => {
      clearTimeout(timer);
    };
  }, [notificationCount, chatCount]);

  return null;
};
